import Button from '@components/common/Button/Button.component'
import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { Form, Modal, Row, Col } from 'react-bootstrap'
import {
  TOOTH_NUMBER,
  isToothNumberAllowedForArchetype,
  ToothSurfaceArchetype,
} from './Appointment.constant'
import { IToothDetails } from '@dataTypes/appointment.type'
import ButtonGroup from '@components/common/ButtonGroup/ButtonGroup.component'
import {
  canAddAnotherSurface,
  getSurfaceSelectionHintText,
  isToothRowSurfaceCountValid,
  normalizeSurfToAtomic,
  pruneSurfForTooth,
  ProcedureSurfaceSelectionRules,
  resolveProcedureSurfaceSelectionRules,
} from '@utils/procedure-surface-selection.utils'
import ToothNumberGrid from './ToothNumberGrid'
import ToothSurfaceSelectionList from './ToothSurfaceSelectionList'

type ApptToothModalProps = {
  isOpen: boolean
  closeModal: () => void
  saveToothSelection: (toothData: IToothDetails[]) => void
  skipToothSelection: () => void
  initialToothSelections?: IToothDetails[]
  procedureSurfaceSelection?: ProcedureSurfaceSelectionRules | null
  procedureSurfaceFilterText?: string | null
  procedureSurfaceArchetype?: ToothSurfaceArchetype | null
  secondaryActionLabel?: 'Cancel' | 'Skip'
}
type TabType = 'arch' | 'tooth-surface' | 'mouth' | 'quadrant' | 'none'

const TAB_MAP: Record<TabType, number> = {
  arch: 0,
  'tooth-surface': 1,
  mouth: 2,
  quadrant: 3,
  none: 4,
}

const normalizeToothSelections = (
  selections: IToothDetails[],
  archetype: ToothSurfaceArchetype,
): IToothDetails[] => {
  return selections
    .filter(
      (selection) =>
        selection.tooth_num === undefined ||
        selection.tooth_num === null ||
        isToothNumberAllowedForArchetype(selection.tooth_num, archetype),
    )
    .map((selection) => ({
      ...selection,
      surf:
        selection.tooth_num !== undefined && selection.tooth_num !== null
          ? pruneSurfForTooth(
              selection.tooth_num,
              normalizeSurfToAtomic(selection.surf),
              archetype,
            )
          : normalizeSurfToAtomic(selection.surf),
    }))
}

const ApptToothModal: React.FC<ApptToothModalProps> = ({
  isOpen,
  closeModal,
  saveToothSelection,
  skipToothSelection,
  initialToothSelections,
  procedureSurfaceSelection = null,
  procedureSurfaceFilterText = null,
  procedureSurfaceArchetype = null,
  secondaryActionLabel = 'Skip',
}) => {
  const [selectedAreaType, setSelectedAreaType] = useState<number | null>(1)
  const [selectedArch, setSelectedArch] = useState<string | null>(null)
  const [selectedMouth, setSelectedMouth] = useState<string | null>(null)
  const [selectedNone, setSelectedNone] = useState<string | null>(null)
  const [selectedQuadrant, setSelectedQuadrant] = useState<string | null>(null)
  const [tempToothSelections, setTempToothSelections] = useState<
    IToothDetails[]
  >([])
  const [activeTab, setActiveTab] = useState<TabType>('tooth-surface')
  const [hasInitialSelections, setHasInitialSelections] = useState(false)

  const tabButtons = [
    { label: '', value: 'tooth-surface', icon: 'mgc_dental_line' },
    { label: '', value: 'arch', svg: 'arch' },
    { label: '', value: 'mouth', icon: 'mgc_mouth_fill' },
    { label: '', value: 'quadrant', svg: 'quadrant' },
    { label: '', value: 'none', icon: 'mgc_close_line' },
  ]

  const handleChangeTab = useCallback((tab: string) => {
    if (tab in TAB_MAP) {
      const validTab = tab as TabType
      setActiveTab(validTab)
      setSelectedAreaType(TAB_MAP[validTab])
      if (validTab === 'mouth') {
        setSelectedMouth('Mouth')
      }
      if (validTab === 'none') {
        setSelectedNone('None')
      }
    }
  }, [])

  const surfaceRules = useMemo((): ProcedureSurfaceSelectionRules => {
    if (procedureSurfaceSelection) {
      return procedureSurfaceSelection
    }
    const resolved = resolveProcedureSurfaceSelectionRules(
      undefined,
      procedureSurfaceFilterText,
    )
    if (
      procedureSurfaceArchetype === 'anterior' ||
      procedureSurfaceArchetype === 'posterior'
    ) {
      return { ...resolved, archetype: procedureSurfaceArchetype }
    }
    return resolved
  }, [
    procedureSurfaceSelection,
    procedureSurfaceFilterText,
    procedureSurfaceArchetype,
  ])

  const requiresSurfaceSelection = surfaceRules.requiresSurfaceSelection
  const surfaceSelectionHint = useMemo(
    () => getSurfaceSelectionHintText(surfaceRules),
    [surfaceRules],
  )
  const toothSurfaceArchetype = surfaceRules.archetype

  const selectedToothNums = useMemo(
    (): ReadonlySet<number> =>
      new Set(
        tempToothSelections
          .map((row) => row.tooth_num)
          .filter((n): n is number => n !== undefined && n !== null),
      ),
    [tempToothSelections],
  )

  useEffect(() => {
    if (!isOpen || toothSurfaceArchetype === 'unknown') {
      return
    }
    setTempToothSelections((prev) =>
      normalizeToothSelections(prev, toothSurfaceArchetype),
    )
  }, [isOpen, toothSurfaceArchetype])

  useEffect(() => {
    if (!isOpen) {
      return
    }
    const initialSelections = initialToothSelections ?? []
    const normalizedInitialSelections = normalizeToothSelections(
      initialSelections,
      toothSurfaceArchetype,
    )
    const firstSelection = normalizedInitialSelections[0]
    const initialAreaType = firstSelection?.area_type ?? 1
    const initialTab: TabType =
      initialAreaType === 0
        ? 'arch'
        : initialAreaType === 2
          ? 'mouth'
          : initialAreaType === 3
            ? 'quadrant'
            : initialAreaType === 4
              ? 'none'
              : 'tooth-surface'

    setActiveTab(initialTab)
    setSelectedAreaType(initialAreaType)
    setSelectedArch(
      initialAreaType === 0 ? (firstSelection?.area ?? null) : null,
    )
    setSelectedMouth(
      initialAreaType === 2 ? (firstSelection?.area ?? 'Mouth') : null,
    )
    setSelectedQuadrant(
      initialAreaType === 3 ? (firstSelection?.area ?? null) : null,
    )
    setSelectedNone(
      initialAreaType === 4 ? (firstSelection?.area ?? 'None') : null,
    )
    setTempToothSelections(
      initialAreaType === 1 ? normalizedInitialSelections : [],
    )
    setHasInitialSelections(normalizedInitialSelections.length > 0)
  }, [initialToothSelections, isOpen, toothSurfaceArchetype])

  const handleArchClick = (arch: string): void => {
    setSelectedArch(arch)
  }

  const handleQuadrantClick = (quadrant: string): void => {
    setSelectedQuadrant(quadrant)
  }

  const handleToothClick = (tooth: number): void => {
    if (!isToothNumberAllowedForArchetype(tooth, toothSurfaceArchetype)) {
      return
    }
    setTempToothSelections((prev) => {
      const existingIndex = prev.findIndex((row) => row.tooth_num === tooth)
      if (existingIndex !== -1) {
        return prev.filter((row) => row.tooth_num !== tooth)
      }
      return [
        ...prev,
        {
          area_type: selectedAreaType as number,
          tooth_num: tooth,
          surf: [],
        },
      ]
    })
  }

  const handleSurfaceToggle = (toothNum: number, surface: string): void => {
    if (!requiresSurfaceSelection) {
      return
    }
    setTempToothSelections((prev) =>
      prev.map((row) => {
        if (row.tooth_num !== toothNum) {
          return row
        }
        const current = row.surf ?? []
        if (current.includes(surface)) {
          return { ...row, surf: current.filter((s) => s !== surface) }
        }
        if (
          !canAddAnotherSurface(
            current.length,
            surfaceRules.surfaceCount,
            surfaceRules.surfaceCountIsMin,
          )
        ) {
          return row
        }
        return { ...row, surf: [...current, surface] }
      }),
    )
  }

  const handleRemoveRow = (toothToRemove: number): void => {
    setTempToothSelections((prev) =>
      prev.filter((item) => item.tooth_num !== toothToRemove),
    )
  }

  const areSavedRowsValid = useMemo((): boolean => {
    if (!requiresSurfaceSelection) {
      return true
    }
    return tempToothSelections.every((row) =>
      isToothRowSurfaceCountValid(row.surf, surfaceRules),
    )
  }, [requiresSurfaceSelection, surfaceRules, tempToothSelections])

  const resetModalState = (): void => {
    setActiveTab('tooth-surface')
    setSelectedAreaType(1)
    setSelectedArch(null)
    setSelectedQuadrant(null)
    setTempToothSelections([])
    setHasInitialSelections(false)
  }

  const handleSave = (): void => {
    let selectedAreaValue = null
    switch (selectedAreaType) {
      case 0:
        selectedAreaValue = selectedArch
        break
      case 2:
        selectedAreaValue = selectedMouth
        break
      case 3:
        selectedAreaValue = selectedQuadrant
        break
      case 4:
        selectedAreaValue = selectedNone
        break
    }
    if (selectedAreaValue !== null) {
      saveToothSelection([
        {
          area_type: selectedAreaType as number,
          area: selectedAreaValue as string,
        },
      ])
    } else {
      saveToothSelection(
        tempToothSelections.map((row) => ({
          ...row,
          surf:
            row.tooth_num !== undefined && row.tooth_num !== null
              ? pruneSurfForTooth(
                  row.tooth_num,
                  row.surf,
                  toothSurfaceArchetype,
                )
              : normalizeSurfToAtomic(row.surf),
        })),
      )
    }
    resetModalState()
    closeModal()
  }

  const handleSecondaryAction = (): void => {
    resetModalState()
    if (secondaryActionLabel === 'Skip') {
      skipToothSelection()
    }
    closeModal()
  }

  const onModalClose = (): void => {
    resetModalState()
    closeModal()
  }

  const isButtonDisabled = (): boolean => {
    switch (selectedAreaType) {
      case 0:
        return selectedArch === null
      case 1:
        if (tempToothSelections.length === 0 && !hasInitialSelections) {
          return true
        }
        return !areSavedRowsValid
      case 2:
      case 4:
        return false
      case 3:
        return selectedQuadrant === null
      default:
        return false
    }
  }

  return (
    <Modal
      show={isOpen}
      onHide={onModalClose}
      size="sm"
      className="appt-tooth-modal"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Body>
        <Form.Label className="tooth-info-title-label">Area type</Form.Label>
        <Row className="mt-1">
          <Col>
            <ButtonGroup
              buttons={tabButtons}
              activeTab={activeTab}
              onButtonClick={handleChangeTab}
              className="area-type-tabs-group"
            />
          </Col>
        </Row>
        {activeTab === 'arch' && (
          <>
            <Row className="area-type-details-row">
              <Col>
                <Form.Label className="tooth-info-details-label">
                  Select arch
                </Form.Label>
              </Col>
            </Row>
            <div className="area-type-details-arch">
              <Row>
                <Col className="mb-1">
                  <Button
                    size="md"
                    className={`button-secondary ${selectedArch === 'Upper Arch' ? 'selected' : ''}`}
                    onClick={() => handleArchClick('Upper Arch')}
                  >
                    Upper arch
                  </Button>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Button
                    size="md"
                    className={`button-secondary ${selectedArch === 'Lower Arch' ? 'selected' : ''}`}
                    onClick={() => handleArchClick('Lower Arch')}
                  >
                    Lower arch
                  </Button>
                </Col>
              </Row>
            </div>
          </>
        )}
        {activeTab === 'tooth-surface' && (
          <>
            <Row className="area-type-details-row">
              <Col>
                <Form.Label className="tooth-info-details-label">
                  Tooth number
                </Form.Label>
              </Col>
            </Row>
            <div className="area-type-details-tooth-surface">
              <ToothNumberGrid
                selectedToothNums={selectedToothNums}
                toothSurfaceArchetype={toothSurfaceArchetype}
                onToothClick={handleToothClick}
              />
              <ToothSurfaceSelectionList
                selections={tempToothSelections}
                toothSurfaceArchetype={toothSurfaceArchetype}
                requiresSurfaceSelection={requiresSurfaceSelection}
                surfaceRules={surfaceRules}
                onSurfaceToggle={handleSurfaceToggle}
                onRemove={handleRemoveRow}
              />
              {requiresSurfaceSelection && surfaceSelectionHint && (
                <div
                  className="appt-tooth-surface-selection-hint"
                  role="status"
                  aria-live="polite"
                >
                  {surfaceSelectionHint}
                </div>
              )}
            </div>
          </>
        )}
        {activeTab === 'mouth' && (
          <>
            <Row className="area-type-details-row">
              <Col>
                <Form.Label className="tooth-info-details-label">
                  Tooth number
                </Form.Label>
              </Col>
            </Row>
            <div className="area-type-details-mouth">
              <div className="appt-tooth">
                {TOOTH_NUMBER.map((item, index) => (
                  <div key={index} className="appt-tooth-item selected">
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
        {activeTab === 'quadrant' && (
          <>
            <Row className="area-type-details-row">
              <Col>
                <Form.Label className="tooth-info-details-label">
                  Select quadrant
                </Form.Label>
              </Col>
            </Row>
            <div className="area-type-details-quadrant">
              <Row>
                <Col className="mb-1 upper-left">
                  <Button
                    size="md"
                    className={`button-secondary ${selectedQuadrant === 'Upper Left' ? 'selected' : ''}`}
                    onClick={() => handleQuadrantClick('Upper Left')}
                  >
                    Upper left
                  </Button>
                </Col>
                <Col className="mb-1">
                  <Button
                    size="md"
                    className={`button-secondary ${selectedQuadrant === 'Upper Right' ? 'selected' : ''}`}
                    onClick={() => handleQuadrantClick('Upper Right')}
                  >
                    Upper right
                  </Button>
                </Col>
              </Row>
              <Row>
                <Col className="lower-left">
                  <Button
                    size="md"
                    className={`button-secondary ${selectedQuadrant === 'Lower Left' ? 'selected' : ''}`}
                    onClick={() => handleQuadrantClick('Lower Left')}
                  >
                    Lower left
                  </Button>
                </Col>
                <Col>
                  <Button
                    size="md"
                    className={`button-secondary ${selectedQuadrant === 'Lower Right' ? 'selected' : ''}`}
                    onClick={() => handleQuadrantClick('Lower Right')}
                  >
                    Lower right
                  </Button>
                </Col>
              </Row>
            </div>
          </>
        )}
        {activeTab === 'none' && <></>}
      </Modal.Body>

      <Modal.Footer>
        <Button
          size="md"
          className="button-secondary me-2"
          onClick={handleSecondaryAction}
        >
          {secondaryActionLabel}
        </Button>
        <Button
          size="md"
          className="button-primary"
          onClick={handleSave}
          disabled={isButtonDisabled()}
        >
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ApptToothModal
